/**
 * Bundle-download trigger for the stems portal.
 *
 * Server bundles every ready stem into a .zip, returns a signed URL, and the
 * client triggers the browser download. Status is surfaced via a Toast and a
 * disabled state on the button while the request is in flight.
 */
import { Button, useToast } from '@ohear/ui'
import { useBundleDownload } from '../../lib/api/stems'

export interface DownloadAllButtonProps {
  projectId: string
  /** Disable when there are zero ready stems. */
  hasReadyStems: boolean
}

export function DownloadAllButton({ projectId, hasReadyStems }: DownloadAllButtonProps) {
  const bundle = useBundleDownload(projectId)
  const toast = useToast()

  const start = () => {
    bundle.mutate(undefined, {
      onSuccess: (data) => {
        toast.show({
          severity: 'success',
          title: 'Bundle ready',
          description: `${data.stem_count} stems queued for download.`,
        })
        // Trigger the actual download. <a download> respects same-origin only;
        // for signed URLs the browser will follow the Content-Disposition header.
        window.location.assign(data.url)
      },
      onError: (err) => {
        toast.show({
          severity: 'error',
          title: 'Could not bundle stems',
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      },
    })
  }

  return (
    <Button
      variant="primary"
      onClick={start}
      loading={bundle.isPending}
      disabled={!hasReadyStems || bundle.isPending}
    >
      Download all (.zip)
    </Button>
  )
}
